import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, GraduationCap, HeartHandshake, MessageSquare, Send } from 'lucide-react';
import { chatAPI, Conversation, Message } from '../services/chatService';
import { useAuthStore } from '../store/authStore';
import { fetchMedewerkerVpStatus, stuurHulpoproep } from '../services/vertrouwenspersoonApi';

/**
 * Berichten (Menu 5, Tab A): 1-op-1 threads en groepsberichten van opleiders over de Academy-trainingen
 * waarvoor de medewerker staat ingeschreven, plus de hulpoproep-chat met de vertrouwenspersoon
 * (type vp_melding, migratie 162). De werkgever kan hier niet meelezen.
 */
const GESPREK_TYPES = ['opleider_professional', 'opleider_groep', 'vp_melding'];

export const MedewerkerBerichtenPage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [actief, setActief] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [invoer, setInvoer] = useState('');
  const [bezig, setBezig] = useState(false);
  const eindRef = useRef<HTMLDivElement>(null);

  const gesprekken = useQuery({ queryKey: ['conversations'], queryFn: chatAPI.getConversations, refetchInterval: 15_000 });
  const lijst = (gesprekken.data ?? []).filter((c) => GESPREK_TYPES.includes(c.type));
  const vpStatus = useQuery({ queryKey: ['medewerker-vp-status'], queryFn: fetchMedewerkerVpStatus, retry: false });

  const openGesprek = (id: string) => {
    const c = (gesprekken.data ?? []).find((g) => g.id === id);
    setActief(c ?? ({ id, type: 'vp_melding', onderwerp: 'Hulpoproep', is_active: true, created_at: new Date().toISOString() } as Conversation));
    setMessages([]);
  };

  const laad = async (id: string) => {
    try {
      setMessages(await chatAPI.getMessages(id));
      setTimeout(() => eindRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e) {
      console.error('Error loading messages', e);
    }
  };

  const actiefId = actief?.id;
  useEffect(() => {
    if (!actiefId) return;
    laad(actiefId);
    const iv = setInterval(() => laad(actiefId), 4000);
    return () => clearInterval(iv);
    // laad gebruikt alleen setters en refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actiefId]);

  const verstuur = async () => {
    if (!invoer.trim() || !actief || bezig) return;
    setBezig(true);
    try {
      await chatAPI.sendMessage(actief.id, invoer.trim());
      setInvoer('');
      await laad(actief.id);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } catch (e) {
      console.error('Error sending message', e);
    } finally {
      setBezig(false);
    }
  };

  const partnerNaam = (c: Conversation) =>
    c.type === 'vp_melding'
      ? `${t('berichten.vertrouwenspersoon', { defaultValue: 'Vertrouwenspersoon' })} ${`${c.participant?.first_name ?? ''} ${c.participant?.last_name ?? ''}`.trim()}`.trim()
      : c.type === 'opleider_groep'
      ? c.onderwerp || c.group_name || t('berichten.groep', { defaultValue: 'Groepsbericht' })
      : `${c.participant?.first_name ?? ''} ${c.participant?.last_name ?? ''}`.trim() || t('berichten.opleider', { defaultValue: 'Opleider' });

  if (actief) {
    return (
      <div className="flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 12rem)' }}>
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
          <button type="button" onClick={() => { setActief(null); queryClient.invalidateQueries({ queryKey: ['conversations'] }); }}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" aria-label={t('berichten.terug', { defaultValue: 'Terug' })}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{partnerNaam(actief)}</p>
            {actief.type === 'vp_melding' ? (
              <p className="text-xs text-gray-500 truncate">
                {t('berichten.vpWis', { defaultValue: 'Vertrouwelijk · berichten worden na 24 uur gewist' })}
              </p>
            ) : (
              actief.type !== 'opleider_groep' && <p className="text-xs text-gray-500 truncate">{actief.onderwerp}</p>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {messages.map((m) => {
            const eigen = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${eigen ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words ${eigen ? 'bg-[#0B1F3A] text-white rounded-br-none' : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                  {m.content}
                  <span className={`block mt-1 text-[10px] ${eigen ? 'text-blue-100' : 'text-gray-400'}`}>
                    {new Date(m.created_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })}
          <div ref={eindRef} />
        </div>
        <div className="p-2 border-t border-gray-100 flex gap-2">
          <input value={invoer} onChange={(e) => setInvoer(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && verstuur()}
            placeholder={t('berichten.placeholder', { defaultValue: 'Typ een bericht…' })}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/20" />
          <button type="button" onClick={verstuur} disabled={!invoer.trim() || bezig}
            className="p-2 rounded-lg bg-[#0B1F3A] text-white disabled:opacity-40" aria-label={t('berichten.verstuur', { defaultValue: 'Verstuur' })}>
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-lg font-semibold text-gray-900">{t('berichten.titel', { defaultValue: 'Berichten' })}</h1>
      <p className="text-sm text-gray-500 mb-4">{t('berichten.intro', { defaultValue: 'Berichten van je vertrouwenspersoon en de opleiders van jouw trainingen. Je werkgever kan niet meelezen.' })}</p>
      {vpStatus.data && vpStatus.data.vertrouwenspersonen.length > 0 && (
        <HulpoproepBlok status={vpStatus.data} onGeopend={(id) => { gesprekken.refetch(); vpStatus.refetch(); openGesprek(id); }} />
      )}
      {gesprekken.isLoading ? (
        <div className="p-6 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
      ) : lijst.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{t('berichten.leeg', { defaultValue: 'Nog geen berichten.' })}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {lijst.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => { setActief(c); setMessages([]); }}
                className="w-full flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left hover:border-gray-300">
                <span className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                  {c.type === 'vp_melding' ? <HeartHandshake className="w-4 h-4 text-[#0B1F3A]" /> : <GraduationCap className="w-4 h-4 text-[#0B1F3A]" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${c.unread_count ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>{partnerNaam(c)}</span>
                    {!!c.unread_count && <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-[#0B1F3A] text-[11px] font-semibold text-white flex items-center justify-center">{c.unread_count}</span>}
                  </span>
                  <span className="block text-xs text-gray-500 truncate">{c.last_message || c.onderwerp}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Hulpoproep naar de vertrouwenspersoon; bij een actieve Waakvlam met 12-uurs reactiegarantie. */
const HulpoproepBlok: React.FC<{
  status: Awaited<ReturnType<typeof fetchMedewerkerVpStatus>>;
  onGeopend: (conversationId: string) => void;
}> = ({ status, onGeopend }) => {
  const { t } = useTranslation('medewerker');
  const [tekst, setTekst] = useState('');
  const vp = status.vertrouwenspersonen.find((v) => v.waakvlam) ?? status.vertrouwenspersonen[0];
  const verstuur = useMutation({
    mutationFn: () => stuurHulpoproep(tekst.trim(), vp.professional_id),
    onSuccess: (r) => {
      setTekst('');
      onGeopend(r.conversation_id);
    },
  });

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0"><HeartHandshake className="w-4 h-4 text-[#0B1F3A]" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {t('berichten.vpTitel', { defaultValue: 'Vertrouwenspersoon' })}: {vp.naam}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            {t('berichten.vpUitleg', { defaultValue: 'Ongewenst gedrag, intimidatie of een integriteitskwestie? Je bericht gaat alleen naar de vertrouwenspersoon. Je werkgever ziet niet wie er contact opneemt of wat je schrijft.' })}
          </p>
          {vp.waakvlam && (
            <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
              <Clock className="w-3 h-3" /> {t('berichten.vpSla', { defaultValue: 'Reactie gegarandeerd binnen {{uren}} uur', uren: status.sla_uren })}
            </p>
          )}
        </div>
      </div>
      {vp.conversation_id ? (
        <button type="button" onClick={() => onGeopend(vp.conversation_id!)} className="mt-3 w-full rounded-lg bg-[#0B1F3A] px-3 py-2 text-sm font-medium text-white">
          {t('berichten.vpOpen', { defaultValue: 'Open je gesprek' })}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <textarea value={tekst} onChange={(e) => setTekst(e.target.value)} rows={3} maxLength={4000}
            placeholder={t('berichten.vpPlaceholder', { defaultValue: 'Beschrijf kort wat er speelt…' })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/20" />
          {verstuur.isError && <p className="text-xs text-red-700">{(verstuur.error as any)?.response?.data?.message ?? 'Versturen mislukt'}</p>}
          <button type="button" disabled={!tekst.trim() || verstuur.isPending} onClick={() => verstuur.mutate()}
            className="w-full rounded-lg bg-[#0B1F3A] px-3 py-2 text-sm font-medium text-white disabled:opacity-40">
            {t('berichten.vpVerstuur', { defaultValue: 'Verstuur hulpoproep' })}
          </button>
          <p className="text-[11px] text-gray-500">{t('berichten.vp112', { defaultValue: 'Direct gevaar? Bel 112.' })}</p>
        </div>
      )}
    </div>
  );
};

export default MedewerkerBerichtenPage;
